<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  dutyRegionLabel,
  dutySupportQuestionLabel,
} from '../utils/dutySupportTemplates';

// Lista "Em atendimento": comentário do atendimento, conclusão/transferência e
// sugestão do Captain para cada conversa já assumida pelo corretor.
defineProps({
  conversations: { type: Array, default: () => [] },
  commentFor: { type: Function, required: true },
  savingId: { type: String, default: null },
  captainSuggestions: { type: Object, default: () => ({}) },
  captainLoadingId: { type: [Number, String], default: null },
});

const emit = defineEmits([
  'open',
  'suggest',
  'copy-suggestion',
  'update-comment',
  'update-status',
]);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Em atendimento">
    <div v-if="conversations.length" class="grid gap-3">
      <article
        v-for="conversation in conversations"
        :key="conversation.id"
        class="rounded-lais border border-lais-border-soft bg-white p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="truncate text-[15px] font-semibold text-lais-text">
              {{ conversation.contact?.name || 'Cliente sem nome' }}
            </p>
            <p class="mt-1 truncate text-[12px] text-lais-text-muted">
              {{ conversation.inbox?.name || 'Inbox' }}
            </p>
            <div class="mt-2 flex flex-wrap items-center gap-1.5">
              <span
                v-if="dutyRegionLabel(conversation)"
                class="rounded-full bg-lais-teal/10 px-2 py-0.5 text-[11px] font-semibold text-lais-teal"
              >
                {{ dutyRegionLabel(conversation) }}
              </span>
              <span
                v-if="dutySupportQuestionLabel(conversation)"
                class="max-w-full truncate rounded-full bg-lais-primary-50 px-2 py-0.5 text-[11px] font-medium text-lais-primary"
              >
                {{ dutySupportQuestionLabel(conversation) }}
              </span>
            </div>
          </div>
          <span
            class="rounded-full bg-lais-primary-50 px-3 py-1 text-[12px] font-semibold text-lais-primary"
          >
            {{ conversation.priority || 'normal' }}
          </span>
        </div>
        <div class="mt-4">
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-external-link"
            size="sm"
            @click="emit('open', conversation)"
          >
            Abrir conversa
          </LaisPrimaryButton>
          <LaisPrimaryButton
            class="ml-2"
            variant="ghost"
            icon="i-lucide-sparkles"
            size="sm"
            :is-loading="captainLoadingId === conversation.id"
            @click="emit('suggest', conversation)"
          >
            Sugerir resposta
          </LaisPrimaryButton>
        </div>
        <div
          class="mt-4 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-3"
        >
          <label
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Comentário do atendimento
          </label>
          <textarea
            :value="commentFor(conversation)"
            rows="3"
            class="mt-2 w-full resize-none rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
            placeholder="Registre o contexto antes de concluir ou transferir."
            @input="emit('update-comment', conversation, $event.target.value)"
          />
          <div class="mt-3 flex flex-wrap gap-2">
            <LaisPrimaryButton
              icon="i-lucide-check-circle"
              size="sm"
              :is-loading="savingId === `${conversation.id}:complete`"
              @click="emit('update-status', conversation, 'complete')"
            >
              Marcar concluído
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-arrow-right-left"
              size="sm"
              :is-loading="savingId === `${conversation.id}:transferred`"
              @click="emit('update-status', conversation, 'transferred')"
            >
              Transferir
            </LaisPrimaryButton>
          </div>
        </div>
        <div
          v-if="captainSuggestions[conversation.id]"
          class="mt-4 rounded-lais border border-lais-primary/20 bg-lais-primary-50/40 p-3"
        >
          <p class="text-[12px] font-semibold text-lais-primary">Captain</p>
          <p class="mt-2 text-[13px] leading-relaxed text-lais-text-body">
            {{ captainSuggestions[conversation.id] }}
          </p>
          <LaisPrimaryButton
            class="mt-3"
            variant="ghost"
            icon="i-lucide-copy"
            size="sm"
            @click="emit('copy-suggestion', conversation)"
          >
            Copiar sugestão
          </LaisPrimaryButton>
        </div>
      </article>
    </div>
    <p v-else class="py-12 text-center text-[13px] text-lais-text-muted">
      Você ainda não iniciou atendimentos neste plantão.
    </p>
  </LaisCard>
</template>
